import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { listInvoices } from '../../api/purchase'
import Spinner from '../../components/Spinner'
import { FilterChips, StatusBadge } from '../../components/PageParts'
import { fmtDay, parseWallClock } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import PurchaseTabs from './PurchaseTabs'
import { invoiceState } from './purchaseShared'
import { useSession } from '../../session'
import { ItemForToggle, setParam, useItemFor } from './itemFor'

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'yours', label: 'Needs you' },
  { id: 'accounting', label: 'With Accounting' },
  { id: 'unpaid', label: 'To be paid' },
  { id: 'paid', label: 'Paid' },
  { id: 'closed', label: 'Rejected or void' },
]

const TYPES = [
  { id: '', label: 'Products and services' },
  { id: 'PO', label: 'Products (with a PO)' },
  { id: 'NonPO', label: 'Services (no PO)' },
]

export default function InvoiceList() {
  const nav = useNavigate()
  const { has } = useSession()
  const products = has('purchase') // without Purchase every vendor invoice is a service one
  const [params, setParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [type, setType] = useState('')
  const filter = FILTERS.some((f) => f.id === params.get('show')) ? params.get('show') : 'all'
  const setFilter = (id) => setParam(setParams, 'show', id)
  const itemFor = useItemFor()

  useEffect(() => {
    setLoading(true)
    listInvoices(undefined, type || undefined)
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [type])

  // With Production: for resale, for materials, or all.
  const mine = useMemo(() => rows.filter(itemFor.matches), [rows, itemFor.value]) // eslint-disable-line react-hooks/exhaustive-deps
  const counts = useMemo(() => Object.fromEntries(
    FILTERS.map((f) => [f.id, mine.filter((r) => f.id === 'all' || invoiceState(r).group === f.id).length])
  ), [mine])

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return mine
      .filter((r) => (filter === 'all' || invoiceState(r).group === filter)
        && (!term || [r.invoice_number, docNo('bill', r.bill_no), r.vendor_name, docNo('po', r.po_no)].join(' ').toLowerCase().includes(term)))
      .sort((a, b) => (parseWallClock(b.invoice_date)?.getTime() || 0) - (parseWallClock(a.invoice_date)?.getTime() || 0) || num(b.invoice_id) - num(a.invoice_id))
  }, [mine, filter, search])

  return (
    <div className="ci-page">
      <PurchaseTabs />
      <div className="ci-header-row">
        <div className="ci-title-block">
          <div>
            <h1>Vendor invoices</h1>
            <div className="ci-title-meta">Bills from vendors. Enter them here and send them to Accounting to check and pay.</div>
          </div>
        </div>
        <div className="ci-header-actions">
          <Link className="ci-btn ci-btn-primary" to={`/purchase/invoices/new${itemFor.query}`}><Plus size={15} />New vendor invoice</Link>
        </div>
      </div>

      <div className="ci-toolbar">
        <label className="ci-search" aria-label="Search vendor invoices">
          <Search size={14} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={products ? "Invoice number, vendor or PO…" : "Invoice number or vendor…"} />
        </label>
        {products && (
          <select className="ci-select" value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
            {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        )}
        <ItemForToggle itemFor={itemFor} rows={rows} />
      </div>

      <FilterChips options={FILTERS.map((f) => ({ ...f, count: counts[f.id] }))} value={filter} onChange={setFilter} />

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Vendor</th>
                <th>Date</th>
                <th>For</th>
                <th className="center">Status</th>
                <th className="num">Total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6"><Spinner label="Loading vendor invoices…" /></td></tr>
              ) : shown.length === 0 ? (
                <tr><td colSpan="6"><div className="ci-empty">{rows.length ? 'No vendor invoices match.' : 'No vendor invoices yet.'}</div></td></tr>
              ) : shown.map((r) => {
                const st = invoiceState(r)
                return (
                  <tr key={r.invoice_id} className="row-link" onClick={() => nav(`/purchase/invoices/${r.invoice_id}`)}>
                    <td>
                      <Link className="ci-je-link" to={`/purchase/invoices/${r.invoice_id}`} onClick={(e) => e.stopPropagation()}>{r.invoice_number || docNo('bill', r.bill_no)}</Link>
                    </td>
                    <td className="ci-strong">{r.vendor_name || '–'}</td>
                    <td className="ci-muted-cell">{fmtDay(r.invoice_date) || '–'}</td>
                    <td>{r.invoice_type === 'NonPO' ? 'Services' : (r.po_id ? `Products · ${docNo('po', r.po_no)}` : 'Products')}</td>
                    <td className="center"><StatusBadge label={st.label} tone={st.tone} /></td>
                    <td className="num ci-total">{money(r.total_amount)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="ci-pagination">
          <span className="ci-page-info">{shown.length} {shown.length === 1 ? 'invoice' : 'invoices'} · {money(shown.reduce((s, r) => s + num(r.total_amount), 0))}</span>
        </div>
      </div>
    </div>
  )
}
